import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { RowDecision } from './api-decisions';
import type { ReportExpenseRow } from './types';

export type OnRowDecision = (row: ReportExpenseRow, kind: RowDecision) => void;

/** «Rendi rimborsabile…» and «Rifiuta» on one row, each only when the row's `can` allows. */
export function RowDecisions({
  row,
  name,
  busy,
  onDecide,
  compact,
}: {
  readonly row: ReportExpenseRow;
  readonly name: string;
  readonly busy: boolean;
  readonly onDecide: OnRowDecision;
  readonly compact?: boolean;
}) {
  const { t } = useTranslation('expenses');
  const action = (kind: RowDecision, key: 'makeReimbursable' | 'rejectExpense') => {
    const label = t(`reports.page.decision.${key}`);
    const aria = t('reports.page.decision.rowAction', { action: label, name });
    return compact ? (
      <button
        type="button"
        className="text-sm underline disabled:opacity-50"
        disabled={busy}
        aria-label={aria}
        onClick={() => onDecide(row, kind)}
      >
        {label}
      </button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={busy}
        aria-label={aria}
        onClick={() => onDecide(row, kind)}
      >
        {label}
      </Button>
    );
  };
  return (
    <>
      {row.can?.makeReimbursable === true ? action('make-reimbursable', 'makeReimbursable') : null}
      {row.can?.reject === true ? action('reject', 'rejectExpense') : null}
    </>
  );
}

/** What an approver wrote on a row: «rifiutata — «nota»», or why it was made reimbursable. */
export function RowNote({ row }: { readonly row: ReportExpenseRow }) {
  const { t } = useTranslation('expenses');
  const note = row.rejected ? row.rejection?.note : row.reimbursableOverride?.note;
  if (!note) return null;
  const key = row.rejected ? 'rejectedWith' : 'madeWith';
  return (
    <small className="text-muted-foreground block">
      {t(`reports.page.decision.${key}`, { note })}
    </small>
  );
}
